import React from 'react';
import { Row, Col, Progress } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserTag } from '@fortawesome/free-solid-svg-icons';
import { booleanTranslator } from '@s20/common/util/string';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    DataGridRefreshButton,
    LinkCellRenderer,
    TextFilterDefaults,
} from '../common/dataGrid/DataGrid';
import { Can } from '../Can';
import CommonContext, { ApiRoutes, agGridConstants } from '../Common';
import {
    FlexRow,
    FlexStartRow,
    FlexEndRow,
    PageHeading,
    SmallButton,
    onReactSelectChanged,
    onFieldChange,
    PageWrap,
    toasty,
} from '../common/forms/FormElements';
import { handleFormSaveError } from '../common/forms/ValidationError';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import { DailyAttendanceEvent } from './DailyAttendanceEvent';
import DailyAttendanceEventForm from './DailyAttendanceEventForm';

const notifySuccess = () => toasty.success(
    'Daily Attendance Event Saved',
    'Daily Attendance Event saved successfully.',
);

class DailyAttendanceEventIndex extends React.Component {
    // bugbug: the index/edit components were borrowed from an outdated component,
    // so don't go patterning other pieces off of this
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        const params = new URLSearchParams(props.location.search);
        const openNew = params.get('new') === 'true';

        this.state = {
            loading: true,
            types: [],
            selectedDailyAttendanceEvent: openNew
                ? new DailyAttendanceEvent()
                : null,
            showDailyAttendanceEventForm: openNew,
        };
        this.onChange = this.onChange.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onAddDailyAttendanceEvent = () => {
        this.setState({
            selectedDailyAttendanceEvent: new DailyAttendanceEvent(),
            showDailyAttendanceEventForm: true,
        });
    };

    onChange = onFieldChange;

    onDailyAttendanceEventDeleteCallback = () => {
        this.onDailyAttendanceEventFormClosed();

        this.state.gridApi.setDatasource(this.state.dataSource);
    };

    onDailyAttendanceEventFormClosed = () => {
        this.setState({
            selectedDailyAttendanceEvent: null,
            showDailyAttendanceEventForm: false,
        });
    };

    onDailyAttendanceEventSaveCallback = async () => {
        this.setState({ saving: true });
        const { selectedDailyAttendanceEvent } = this.state;

        const saveUrl = selectedDailyAttendanceEvent.id
            && selectedDailyAttendanceEvent.id > 0
            ? ApiRoutes.dailyAttendanceEvent.update()
            : ApiRoutes.dailyAttendanceEvent.create();

        const response = await httpClient
            .andGetResponse(
                httpClient.types.post,
                saveUrl,
                selectedDailyAttendanceEvent,
                'Error Saving Daily Attendance Event',
                () => this.setState({ saving: false }),
            )
            .catch(this.handleSaveError);

        if (response && response > 0) {
            notifySuccess();

            this.setState({
                selectedDailyAttendanceEvent: null,
                showDailyAttendanceEventForm: false,
                saving: false,
            });

            this.state.gridApi.setDatasource(this.state.dataSource);
        }
    };

    onEditDailyAttendanceEvent = async (DailyAttendanceEventId) => {
        const selectedDailyAttendanceEvent = await httpClient.js(
            ApiRoutes.dailyAttendanceEvent.byId(DailyAttendanceEventId),
        );

        this.setState({
            selectedDailyAttendanceEvent,
            showDailyAttendanceEventForm: true,
        });
    };

    onSelectChange = onReactSelectChanged;

    clearErrors = () => this.setState({ errors: {} });

    handleSaveError = (err) => {
        console.debug(err);
        handleFormSaveError(this, err);
    };

    populateState() {
        // Get grid defaults and handlers
        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            nameRenderer: LinkCellRenderer,
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
        };

        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        const yesNoFilterParams = {
            suppressFilterButton: true,
            options: yesNo,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: null,
        };

        // Provide column definitions
        gridOptions.columnDefs = [
            {
                colId: 'Name',
                sortable: true,
                headerName: 'Name',
                field: 'name',
                filter: agGridConstants.columnFilterTypes.text,
                floatingFilter: true,
                filterParams: TextFilterDefaults,
                sort: { direction: 'asc', priority: 0 },
                cellRenderer: 'nameRenderer',
                cellRendererParams: {
                    clicked: this.onEditDailyAttendanceEvent,
                    nameField: 'name',
                    idField: 'id',
                    title: 'View this Daily Attendance Event',
                },
            },
            {
                colId: 'active',
                sortable: false,
                headerName: 'Active',
                field: 'active',
                cellRenderer: booleanTranslator,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
            {
                colId: 'employeeMayNotBeAssigned',
                sortable: false,
                headerName: 'Employee May Not Be Assigned For Rest Of Day',
                field: 'employeeMayNotBeAssigned',
                cellRenderer: booleanTranslator,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
        ];

        // Create datasource.  Is reused and rebound on refresh from the state.
        const dataSource = createDataSource(
            ApiRoutes.dailyAttendanceEvent.search(),
            gridOptions,
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
        });
    }

    render() {
        if (this.state.loading) {
            return <Progress />;
        }

        const {
            rowData,
            gridOptions,
            showDailyAttendanceEventForm,
            selectedDailyAttendanceEvent,
        } = this.state;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faUserTag}
                        className="mr-2 text-muted"
                    />
                    <span>Daily Attendance Events</span>
                </PageHeading>
                <Row>
                    <Col>
                        <FlexRow>
                            <FlexStartRow>
                                <DataGridRefreshButton
                                    gridApi={this.state.gridApi}
                                    dataSource={this.state.dataSource}
                                />
                            </FlexStartRow>
                            <FlexEndRow>
                                <Can
                                    I="create"
                                    a="longtermstatus"
                                >
                                    <SmallButton
                                        type="button"
                                        disabled={
                                            showDailyAttendanceEventForm
                                        }
                                        onClick={
                                            this
                                                .onAddDailyAttendanceEvent
                                        }
                                    >
                                        <i className="fa fa-plus-circle fa-md mr-2" />
                                        Add Daily Attendance Event
                                    </SmallButton>
                                </Can>
                            </FlexEndRow>
                        </FlexRow>
                    </Col>
                </Row>
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                />
                <DailyAttendanceEventForm
                    selectedDailyAttendanceEvent={selectedDailyAttendanceEvent}
                    onChange={this.onChange}
                    onSelectChange={this.onSelectChange}
                    onClose={this.onDailyAttendanceEventFormClosed}
                    show={showDailyAttendanceEventForm}
                    onSaveCallback={this.onDailyAttendanceEventSaveCallback}
                    onDeleteCallback={this.onDailyAttendanceEventDeleteCallback}
                />
            </PageWrap>
        );
    }
}

export default DailyAttendanceEventIndex;
